"use client";

import Image from "next/image";
import Link from "next/link";
import { ProductType } from "@/types/product.type";
import routes from "@/app.route";
import SparkleEffect from "./sparkle-effect";
import useNumber from "@/hooks/number";
import { getWithGstAmount } from "@/lib/gst";
import { Button, Chip } from "@mui/material";
import config from "@/app.config";
import { useRouter } from "next/navigation";
import useCart from "@/stores/cart";

export default function ProductCard({ product }: { product: ProductType }) {
  const { currency } = useNumber();
  const router = useRouter();
  const { loading, addItem } = useCart();

  return (
    <div className="group hover:shadow-xl duration-500 border border-zinc-200 bg-white block rounded-lg overflow-hidden">
      <Link href={routes.product.url.replace(":slug", product.handle)}>
        <figure>
          {product.featured_image ? (
            <Image
              src={product.featured_image.original_url}
              alt={product.title}
              width={300}
              height={300}
              className="w-full"
            />
          ) : (
            <div className="md:size-[300px] size-[165px]"></div>
          )}
        </figure>
        <div className="bg-white px-3 py-6 relative group-hover:-translate-y-4 transition-all duration-300">
          <div className="truncate">{product.title}</div>
          {product.variant && (
            <>
              <div className="flex items-center gap-x-2 flex-wrap">
                <div className="text-lg font-semibold">
                  {currency(product.variant.price)}
                </div>
                {product.variant.compare_at_price && (
                  <div className="text-zinc-500 line-through">
                    {currency(
                      getWithGstAmount(
                        product.variant.compare_at_price,
                        product.variant.gst_rate,
                      ).toFixed(0),
                    )}
                  </div>
                )}
              </div>
              {product.variant.compare_at_price ? (
                <div className="font-bold text-green-700">
                  {(
                    ((Number(product.variant.compare_at_price) -
                      Number(product.variant.price)) /
                      Number(product.variant.compare_at_price)) *
                    100
                  ).toFixed(0)}
                  % off
                </div>
              ) : (
                <div className="size-6"></div>
              )}
            </>
          )}
        </div>
      </Link>
      {product.variant && (
        <div className="px-4 pb-4">
          <SparkleEffect>
            {Number(product.variant.price) > 0 &&
            Number(product.variant.available) > 0 ? (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                loading={loading}
                disabled={loading}
                onClick={() => {
                  addItem({ ...product.variant!, quantity: "1" });
                  router.push(routes.checkout.url);
                }}
              >
                Buy Now
              </Button>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                href={`tel:${config.contact.primaryPhone}`}
                target="_blank"
              >
                Enquire Now
              </Button>
            )}
          </SparkleEffect>
        </div>
      )}
    </div>
  );
}
